import React from 'react';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { ChatProspectStage } from '@/types';
import { createStyles } from '@/contexts/theme-context';
import { ProspectDetailAddress } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/types';
import { ProspectDetailPhoneNumber } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/components/prospect-detail-phone-number';
import { ProspectStageMenu } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/components/prospect-stage-menu';
import {
  STAGE_SLOT_MIN_HEIGHT,
  STAGE_SLOT_MIN_WIDTH,
} from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/constants';
import { Typography, YStack } from '@usepepper/sprout-kit-ui';
import { View } from 'react-native';

interface ProspectDetailHeaderProps {
  accountId: string | null;
  address: ProspectDetailAddress | null;
  isPhoneNumberLoading: boolean;
  isStageLoading: boolean;
  name: string;
  onSelectLost: () => void;
  onSelectStage: (stage: ChatProspectStage) => void;
  phoneNumber: string | null;
  stage: ChatProspectStage | null;
}

export const ProspectDetailHeader: React.FC<ProspectDetailHeaderProps> = ({
  accountId,
  address,
  isPhoneNumberLoading,
  isStageLoading,
  name,
  onSelectLost,
  onSelectStage,
  phoneNumber,
  stage,
}) => {
  const [styles, theme] = useStyles();

  const formattedAddress = address
    ? [address.address, address.city, address.region].filter(Boolean).join(', ')
    : null;

  const hasSubtitle = Boolean(accountId || formattedAddress);
  const needSeparator = Boolean(accountId && formattedAddress);

  return (
    <YStack gap="$2">
      <View style={styles.nameRow}>
        <View style={styles.nameColumn}>
          <Typography accessibilityRole="header" size="$lg" variant="heading">
            {name}
          </Typography>
        </View>
        <View style={styles.stageSlot}>
          {isStageLoading ? (
            <AnimatedSkeletonLoader
              borderRadius={theme.sk.radii.radiusRound}
              height={STAGE_SLOT_MIN_HEIGHT}
              width={STAGE_SLOT_MIN_WIDTH}
            />
          ) : stage ? (
            <ProspectStageMenu
              onSelectLost={onSelectLost}
              onSelectStage={onSelectStage}
              stage={stage}
            />
          ) : null}
        </View>
      </View>
      {hasSubtitle ? (
        <Typography
          color="$textSecondary"
          numberOfLines={1}
          size="$sm"
          variant="body"
        >
          {accountId}
          {needSeparator ? ' • ' : null}
          {formattedAddress}
        </Typography>
      ) : null}
      <ProspectDetailPhoneNumber
        isLoading={isPhoneNumberLoading}
        phoneNumber={phoneNumber}
      />
    </YStack>
  );
};

const useStyles = createStyles((theme) => ({
  nameColumn: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    minHeight: STAGE_SLOT_MIN_HEIGHT,
  },
  stageSlot: {
    alignItems: 'flex-end',
  },
}));
